<template>
    <div class="card-page">
        <div class="card-toolbar">
            <div class="card-toolbar__info">
                <span class="card-toolbar__icon">
                    <el-icon>
                        <Key />
                    </el-icon>
                </span>
                <span class="card-toolbar__meta">
                    <span class="card-toolbar__title">权限卡</span>
                    <span class="card-toolbar__subtitle">
                        为一张卡配置资源包和账号后，外部程序用 cardId 调用 /openapi/card 完成申请、登录并读取 packageData
                    </span>
                </span>
            </div>
            <div class="card-toolbar__actions">
                <el-select v-if="activeTab !== 'card'" v-model="selectedCardId" class="card-picker" placeholder="选择权限卡"
                    filterable @change="onCardChange">
                    <el-option v-for="item in cardList" :key="item.id" :label="item.name" :value="item.id" />
                </el-select>
                <el-button class="toolbar-btn toolbar-btn--ghost" :loading="loading" @click="refresh">
                    <el-icon v-if="!loading" class="btn-icon">
                        <Refresh />
                    </el-icon>
                    刷新
                </el-button>
                <el-button v-if="activeTab !== 'log'" class="toolbar-btn" type="primary" @click="openCreate">
                    <el-icon class="btn-icon">
                        <Plus />
                    </el-icon>
                    {{ createLabel }}
                </el-button>
            </div>
        </div>

        <div class="card-switch">
            <button type="button" class="card-switch__item" :class="{ 'is-active': activeTab === 'card' }"
                @click="switchTab('card')">
                <el-icon class="card-switch__icon"><Key /></el-icon>
                <span>权限卡</span>
                <em class="card-switch__count">{{ cardList.length }}</em>
            </button>
            <button type="button" class="card-switch__item" :class="{ 'is-active': activeTab === 'package' }"
                @click="switchTab('package')">
                <el-icon class="card-switch__icon"><Box /></el-icon>
                <span>资源包</span>
                <em class="card-switch__count">{{ packageList.length }}</em>
            </button>
            <button type="button" class="card-switch__item" :class="{ 'is-active': activeTab === 'user' }"
                @click="switchTab('user')">
                <el-icon class="card-switch__icon"><User /></el-icon>
                <span>卡用户</span>
                <em class="card-switch__count">{{ userList.length }}</em>
            </button>
            <button type="button" class="card-switch__item" :class="{ 'is-active': activeTab === 'log' }"
                @click="switchTab('log')">
                <el-icon class="card-switch__icon"><Tickets /></el-icon>
                <span>使用日志</span>
                <em class="card-switch__count">{{ logPage.total }}</em>
            </button>
        </div>

        <section class="card-panel" v-if="activeTab === 'card'" v-loading="loading">
            <el-empty v-if="cardList.length === 0 && !loading" description="还没有权限卡，点击右上角「创建权限卡」开始" />
            <article class="row-item" v-for="row in cardList" :key="row.id"
                :class="{ 'is-selected': selectedCardId === row.id }" @click="selectCard(row)">
                <span class="row-item__icon">
                    <el-icon><Key /></el-icon>
                </span>
                <div class="row-item__main">
                    <div class="row-item__head">
                        <span class="row-item__name">{{ row.name }}</span>
                        <span v-if="selectedCardId === row.id" class="card-chip card-chip--on">当前卡</span>
                    </div>
                    <div class="row-item__desc">{{ row.description || '暂无说明' }}</div>
                    <div class="row-item__meta">
                        <span class="card-chip card-chip--count">资源包 {{ Number(row.packageCount || 0) }}</span>
                        <span class="card-chip card-chip--count">用户 {{ Number(row.memberCount || 0) }}</span>
                        <button v-if="row.inviteCode" type="button" class="id-box" @click.stop="copy(row.inviteCode, '邀请码已复制')">
                            <span>邀请码 {{ row.inviteCode }}</span>
                            <el-icon><CopyDocument /></el-icon>
                        </button>
                        <button type="button" class="id-box" @click.stop="copy(row.id, 'cardId 已复制')">
                            <span>{{ row.id }}</span>
                            <el-icon><CopyDocument /></el-icon>
                        </button>
                    </div>
                </div>
                <div class="row-item__actions" @click.stop>
                    <el-button plain @click="openPackages(row)">资源包</el-button>
                    <el-button plain @click="openUsers(row)">用户</el-button>
                    <el-button plain @click="openEditCard(row)">编辑</el-button>
                    <el-popconfirm width="240" title="删除前需要先清空资源包和用户，确定删除吗？" confirm-button-text="确定删除"
                        cancel-button-text="取消" @confirm="deleteCard(row)">
                        <template #reference>
                            <el-button type="danger" plain>删除</el-button>
                        </template>
                    </el-popconfirm>
                </div>
            </article>
        </section>

        <section class="card-panel" v-else-if="activeTab === 'package'" v-loading="loading">
            <el-empty v-if="!selectedCardId" description="请先选择一张权限卡">
                <el-button type="primary" @click="switchTab('card')">去选择</el-button>
            </el-empty>
            <el-empty v-else-if="packageList.length === 0 && !loading" description="这张卡还没有资源包" />
            <article class="row-item" v-for="row in packageList" :key="row.id" :class="{ 'is-off': !row.isEnable }">
                <span class="row-item__icon">
                    <el-icon><Box /></el-icon>
                </span>
                <div class="row-item__main">
                    <div class="row-item__head">
                        <span class="row-item__name">{{ row.name }}</span>
                        <span class="card-chip" :class="row.isEnable ? 'card-chip--on' : 'card-chip--off'">
                            {{ row.isEnable ? '启用中' : '已停用' }}
                        </span>
                        <span v-if="row.isAllowApply" class="card-chip card-chip--count">可自助申请</span>
                        <span v-if="row.locked" class="card-chip card-chip--lock">锁定中</span>
                    </div>
                    <div class="row-item__desc">{{ row.description || '暂无说明' }}</div>
                    <div class="row-item__meta">
                        <span class="card-chip card-chip--count">已开通 {{ Number(row.memberCount || 0) }} 人</span>
                        <span class="card-chip">个人 {{ quotaText(row.userQuota, row) }}</span>
                        <span class="card-chip">共享 {{ quotaText(row.sharedQuota, row) }}</span>
                        <span class="card-chip">{{ scaleOf(row) }} 位小数</span>
                        <button type="button" class="id-box" @click="copy(row.id, '资源包 ID 已复制')">
                            <span>{{ row.id }}</span>
                            <el-icon><CopyDocument /></el-icon>
                        </button>
                    </div>
                    <pre class="json-preview" :class="{ 'is-collapsed': !isExpanded(row) }">{{ prettyJson(row.packageData) }}</pre>
                    <el-button link type="primary" size="small" @click="toggleExpand(row)">
                        {{ isExpanded(row) ? '收起配置' : '查看配置' }}
                    </el-button>
                </div>
                <div class="row-item__actions">
                    <el-switch v-model="row.isEnable" :loading="!!row._enableLoading" @change="enablePackage(row)" />
                    <el-button plain @click="openEditPackage(row)">编辑</el-button>
                    <el-button v-if="row.locked" plain @click="unlockPackage(row)">解锁</el-button>
                    <el-popconfirm width="230" title="确定删除该资源包吗？" confirm-button-text="确定删除" cancel-button-text="取消"
                        @confirm="deletePackage(row)">
                        <template #reference>
                            <el-button type="danger" plain :disabled="Number(row.memberCount || 0) > 0">删除</el-button>
                        </template>
                    </el-popconfirm>
                </div>
            </article>
        </section>

        <section class="card-panel" v-else-if="activeTab === 'user'" v-loading="loading">
            <el-empty v-if="!selectedCardId" description="请先选择一张权限卡">
                <el-button type="primary" @click="switchTab('card')">去选择</el-button>
            </el-empty>
            <el-empty v-else-if="userList.length === 0 && !loading" description="还没有卡用户，可由管理员添加，或开放自助申请" />
            <article class="row-item" v-for="row in userList" :key="row.id" :class="{ 'is-off': !row.isEnable }">
                <span class="row-item__icon">
                    <el-icon><User /></el-icon>
                </span>
                <div class="row-item__main">
                    <div class="row-item__head">
                        <span class="row-item__name">{{ row.userEmail }}</span>
                        <span class="card-chip" :class="row.isEnable ? 'card-chip--on' : 'card-chip--off'">
                            {{ row.isEnable ? '启用中' : '已停用' }}
                        </span>
                        <span class="card-chip">{{ row.createMode === '0' ? '自助申请' : '管理员添加' }}</span>
                        <span v-if="row.isAdmin" class="card-chip card-chip--lock">管理员</span>
                    </div>
                    <div class="row-item__desc">{{ row.description || '暂无备注' }}</div>
                    <div class="row-item__meta">
                        <span class="card-chip" v-for="name in packageNames(row.packageIds)" :key="name">{{ name }}</span>
                        <span v-if="packageNames(row.packageIds).length === 0" class="card-chip card-chip--off">未开通资源包</span>
                    </div>
                    <div class="row-item__meta">
                        <span class="pwd-box">
                            {{ row._showPwd ? row.userPassword : '••••••••' }}
                        </span>
                        <el-button link type="primary" size="small" @click="row._showPwd = !row._showPwd">
                            {{ row._showPwd ? '隐藏' : '显示密码' }}
                        </el-button>
                        <el-button link size="small" @click="copy(row.userPassword, '密码已复制')">复制密码</el-button>
                    </div>
                </div>
                <div class="row-item__actions">
                    <el-switch v-model="row.isEnable" :loading="!!row._enableLoading" @change="enableUser(row)" />
                    <el-button plain @click="openEditUser(row)">编辑</el-button>
                    <el-button plain @click="resetPassword(row)">重置密码</el-button>
                    <el-popconfirm width="220" title="确定删除该卡用户吗？" confirm-button-text="确定删除" cancel-button-text="取消"
                        @confirm="deleteUser(row)">
                        <template #reference>
                            <el-button type="danger" plain>删除</el-button>
                        </template>
                    </el-popconfirm>
                </div>
            </article>
        </section>

        <section class="card-panel" v-else-if="activeTab === 'log'" v-loading="loading">
            <el-empty v-if="!selectedCardId" description="请先选择一张权限卡">
                <el-button type="primary" @click="switchTab('card')">去选择</el-button>
            </el-empty>
            <template v-else>
                <div class="log-filter">
                    <el-select v-model="logQuery.category" class="log-type" clearable placeholder="记录类型">
                        <el-option label="登录" value="login" />
                        <el-option label="个人额度" value="personal" />
                        <el-option label="共享额度" value="shared" />
                    </el-select>
                    <span>登记时间</span>
                    <el-date-picker v-model="logQuery.bizTimeStart" type="datetime" value-format="YYYY-MM-DD HH:mm:ss"
                        placeholder="开始" />
                    <el-date-picker v-model="logQuery.bizTimeEnd" type="datetime" value-format="YYYY-MM-DD HH:mm:ss"
                        placeholder="结束" />
                    <span>创建时间</span>
                    <el-date-picker v-model="logQuery.createTimeStart" type="datetime" value-format="YYYY-MM-DD HH:mm:ss"
                        placeholder="开始" />
                    <el-date-picker v-model="logQuery.createTimeEnd" type="datetime" value-format="YYYY-MM-DD HH:mm:ss"
                        placeholder="结束" />
                    <el-button type="primary" plain @click="searchLogs">查询</el-button>
                </div>
                <vxe-table ref="logTable" class="log-table" :data="logList" :height="logTableHeight" size="small" border stripe
                    show-overflow auto-resize :row-config="{ keyField: 'id', isHover: true }"
                    :column-config="{ resizable: true }" :scroll-y="{ enabled: true, gt: 40 }">
                    <vxe-column type="seq" width="52" title="#" fixed="left" />
                    <vxe-column field="userEmail" title="用户" min-width="180" />
                    <vxe-column field="categoryName" title="类型" width="100" />
                    <vxe-column field="packageName" title="资源包" min-width="140" />
                    <vxe-column field="delta" title="次数" width="110">
                        <template #default="{ row }">{{ amountText(row.delta, row) }}</template>
                    </vxe-column>
                    <vxe-column field="bizTime" title="登记时间" width="170">
                        <template #default="{ row }">{{ formatTime(row.bizTime) }}</template>
                    </vxe-column>
                    <vxe-column field="createTime" title="创建时间" width="170">
                        <template #default="{ row }">{{ formatTime(row.createTime) }}</template>
                    </vxe-column>
                    <vxe-column field="bizKey" title="业务单号" min-width="140" />
                    <vxe-column field="remark" title="备注" min-width="160" />
                    <vxe-column field="signature" title="设备特征" min-width="140" />
                    <vxe-column field="editHistory" title="修改过程" width="110" :show-overflow="false">
                        <template #default="{ row }">
                            <el-button v-if="historyCount(row)" link type="primary" @click="openHistory(row)">
                                {{ historyCount(row) }} 次修改
                            </el-button>
                            <span v-else class="log-history-empty">无</span>
                        </template>
                    </vxe-column>
                    <vxe-column title="操作" width="88" fixed="right" :show-overflow="false">
                        <template #default="{ row }">
                            <el-button v-if="row.category === 'personal' || row.category === 'shared'" link type="primary"
                                @click="openEditLog(row)">修改</el-button>
                        </template>
                    </vxe-column>
                    <template #empty>
                        <span>{{ logPage.total > 0 ? '这一页没有记录' : '还没有使用记录' }}</span>
                    </template>
                </vxe-table>
                <div class="log-pagination" v-if="logPage.total > 0">
                    <el-pagination background layout="total, prev, pager, next" :total="logPage.total"
                        :page-size="logPage.pageSize" :current-page="logPage.page" @current-change="changeLogPage" />
                </div>
            </template>
        </section>

        <el-dialog :title="cardForm.id ? '编辑权限卡' : '创建权限卡'" v-model="cardVisible" width="520px"
            :close-on-click-modal="false">
            <el-form ref="cardFormRef" :model="cardForm" :rules="cardRules" label-width="88px">
                <el-form-item label="名称" prop="name">
                    <el-input v-model="cardForm.name" maxlength="80" show-word-limit placeholder="例如：门店助手" />
                </el-form-item>
                <el-form-item label="说明">
                    <el-input type="textarea" :rows="3" maxlength="300" show-word-limit v-model="cardForm.description"
                        placeholder="这个应用用权限卡做什么" />
                </el-form-item>
                <el-form-item label="邀请码">
                    <el-input v-model="cardForm.inviteCode" maxlength="64" placeholder="留空则不限制申请" />
                    <span class="form-tip">填写后，自助申请必须带上相同的邀请码。工作台直接添加的用户不需要</span>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="cardVisible = false">取消</el-button>
                <el-button type="primary" :loading="saving" @click="saveCard">保存</el-button>
            </template>
        </el-dialog>

        <el-dialog class="package-dialog" :title="packageForm.id ? '编辑资源包' : '创建资源包'" v-model="packageVisible"
            width="640px" top="12px" :close-on-click-modal="false">
            <el-form ref="packageFormRef" :model="packageForm" :rules="packageRules" label-width="108px">
                <el-form-item label="名称" prop="name">
                    <el-input v-model="packageForm.name" maxlength="80" show-word-limit placeholder="例如：标准配置" />
                </el-form-item>
                <el-form-item label="说明">
                    <el-input type="textarea" :rows="2" maxlength="300" show-word-limit v-model="packageForm.description" />
                </el-form-item>
                <el-form-item label="启用">
                    <el-switch v-model="packageForm.isEnable" />
                    <span class="form-tip">停用后登录不会下发这份配置</span>
                </el-form-item>
                <el-form-item label="自助申请">
                    <el-switch v-model="packageForm.isAllowApply" />
                    <span class="form-tip">开启后外部用户可用邮箱申请</span>
                </el-form-item>
                <el-form-item label="邮箱后缀">
                    <el-input v-model="packageForm.allowEmailSuffix" placeholder="@company.com，留空不限制" />
                </el-form-item>
                <el-form-item label="邮箱白名单">
                    <el-input type="textarea" :rows="2" v-model="packageForm.allowEmail"
                        placeholder="多个邮箱用英文逗号分隔，留空不限制" />
                </el-form-item>
                <el-form-item label="小数位数">
                    <el-input-number v-model="packageForm.quotaScale" :min="0" :max="6" :step="1" :precision="0" />
                    <span class="form-tip">消耗和额度最多保留几位小数，0 表示只能记整数</span>
                </el-form-item>
                <el-form-item label="个人额度">
                    <el-input v-model="packageForm.userQuota" placeholder="不设上限" />
                    <span class="form-tip">每个用户在这个资源包上最多能消耗多少，留空不限制</span>
                </el-form-item>
                <el-form-item label="共享额度">
                    <el-input v-model="packageForm.sharedQuota" placeholder="不设上限" />
                    <span class="form-tip">所有用户加在一起最多能消耗多少，留空不限制</span>
                </el-form-item>
                <el-form-item label="配置 JSON">
                    <el-input class="json-input" type="textarea" :rows="8" v-model="packageForm.packageDataText"
                        placeholder='{"feature": true}' />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="packageVisible = false">取消</el-button>
                <el-button type="primary" :loading="saving" @click="savePackage">保存</el-button>
            </template>
        </el-dialog>

        <el-dialog :title="userForm.id ? '编辑卡用户' : '添加卡用户'" v-model="userVisible" width="640px"
            :close-on-click-modal="false">
            <el-form ref="userFormRef" :model="userForm" :rules="userRules" label-width="88px">
                <el-form-item label="邮箱" prop="userEmail">
                    <el-input v-model="userForm.userEmail" maxlength="255" placeholder="user@company.com" />
                </el-form-item>
                <el-form-item v-if="!userForm.id" label="密码">
                    <el-input v-model="userForm.userPassword" maxlength="255" placeholder="留空则自动生成" />
                </el-form-item>
                <el-form-item label="资源包" prop="packageIds">
                    <el-select v-model="userForm.packageIds" multiple filterable placeholder="选择要开通的资源包" style="width: 100%">
                        <el-option v-for="item in packageList" :key="item.id" :label="item.name" :value="item.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="设备特征">
                    <el-input type="textarea" :rows="2" v-model="userForm.signatures"
                        placeholder="留空不限制设备，示例：[win11-zhangsan]" />
                </el-form-item>
                <el-form-item label="备注">
                    <el-input type="textarea" :rows="2" maxlength="300" v-model="userForm.description" />
                </el-form-item>
                <el-form-item label="启用">
                    <el-switch v-model="userForm.isEnable" />
                </el-form-item>
                <el-form-item label="管理员">
                    <el-switch v-model="userForm.isAdmin" />
                    <span class="form-tip">可通过开放接口启用或停用本卡其他用户，以及修改消耗的次数和备注</span>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="userVisible = false">取消</el-button>
                <el-button type="primary" :loading="saving" @click="saveUser">保存</el-button>
            </template>
            </el-dialog>

        <el-dialog title="修改过程" v-model="historyVisible" width="520px" top="12vh">
            <div v-if="historyRow" class="log-history-meta">
                {{ historyRow.userEmail || '未知用户' }} · {{ historyRow.categoryName || historyRow.category }} · {{ historyRow.packageName || '未知资源包' }}
            </div>
            <el-timeline class="log-history">
                <el-timeline-item v-for="(item, index) in historyList(historyRow)" :key="index" :timestamp="item.time || ''"
                    placement="top">
                    把{{ fieldLabel(item.field) }}从「{{ historyValue(item, 'from') }}」改为「{{ historyValue(item, 'to') }}」
                </el-timeline-item>
            </el-timeline>
        </el-dialog>

        <el-dialog title="修改使用记录" v-model="logVisible" width="520px" :close-on-click-modal="false">
            <el-form label-width="88px">
                <el-form-item label="次数">
                    <el-input v-model="logForm.delta" placeholder="可以为 0，不能为负" />
                </el-form-item>
                <el-form-item label="登记时间">
                    <el-date-picker v-model="logForm.bizTime" type="datetime" value-format="YYYY-MM-DD HH:mm:ss"
                        placeholder="业务发生时间" :clearable="false" style="width: 100%" />
                </el-form-item>
                <el-form-item label="备注">
                    <el-input type="textarea" :rows="3" maxlength="300" v-model="logForm.remark" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="logVisible = false">取消</el-button>
                <el-button type="primary" :loading="saving" @click="saveLog">保存</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '../../../utils/request.js';
import { copy as copyText } from '../../../utils/clipboard.js';
import { Key, Box, User, Tickets, Plus, Refresh, CopyDocument } from '@element-plus/icons-vue';

export default {
    components: { Key, Box, User, Tickets, Plus, Refresh, CopyDocument },
    data() {
        return {
            activeTab: 'card',
            loading: false,
            saving: false,
            selectedCardId: '',
            cardList: [],
            packageList: [],
            userList: [],
            logList: [],
            logQuery: { category: '', bizTimeStart: '', bizTimeEnd: '', createTimeStart: '', createTimeEnd: '' },
            logPage: { page: 1, pageSize: 500, total: 0 },
            logTableHeight: 480,
            expandedIds: [],
            logVisible: false,
            historyVisible: false,
            historyRow: null,
            logForm: { id: '', delta: '', remark: '', bizTime: '' },
            cardVisible: false,
            packageVisible: false,
            userVisible: false,
            cardForm: { id: '', name: '', description: '', inviteCode: '' },
            packageForm: {
                id: '',
                name: '',
                description: '',
                isEnable: true,
                isAllowApply: false,
                allowEmail: '',
                allowEmailSuffix: '',
                packageDataText: '{\n}',
                quotaScale: 0,
                userQuota: '',
                sharedQuota: ''
            },
            userForm: {
                id: '',
                userEmail: '',
                userPassword: '',
                packageIds: [],
                signatures: '',
                description: '',
                isEnable: true,
                isAdmin: false
            },
            cardRules: {
                name: [{ required: true, message: '请输入名称', trigger: 'blur' }]
            },
            packageRules: {
                name: [{ required: true, message: '请输入名称', trigger: 'blur' }]
            },
            userRules: {
                userEmail: [{ required: true, message: '请输入邮箱', trigger: 'blur' }],
                packageIds: [{ type: 'array', required: true, message: '请选择资源包', trigger: 'change' }]
            }
        };
    },
    computed: {
        createLabel() {
            if (this.activeTab === 'package') return '创建资源包';
            if (this.activeTab === 'user') return '添加用户';
            return '创建权限卡';
        }
    },
    mounted() {
        this.syncLogTableHeight();
        window.addEventListener('resize', this.syncLogTableHeight);
        this.loadCards();
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.syncLogTableHeight);
    },
    methods: {
        emptyPackageForm() {
            return {
                id: '',
                name: '',
                description: '',
                isEnable: true,
                isAllowApply: false,
                allowEmail: '',
                allowEmailSuffix: '',
                packageDataText: '{\n}',
                quotaScale: 0,
                userQuota: '',
                sharedQuota: ''
            };
        },
        quotaText(value, row) {
            if (value === null || value === undefined || value === '') return '不限';
            return this.formatAmount(value, this.scaleOf(row));
        },
        scaleOf(row) {
            const scale = row && row.quotaScale !== null && row.quotaScale !== undefined ? Number(row.quotaScale) : 0;
            if (!Number.isInteger(scale) || scale < 0 || scale > 6) return 0;
            return scale;
        },
        formatAmount(value, scale) {
            const text = value === null || value === undefined ? '' : String(value).trim();
            if (!text) return '';
            const digits = this.scaleOf({ quotaScale: scale });
            const match = text.match(/^(-?)(\d+)(?:\.(\d+))?$/);
            if (!match) return text;
            const negative = match[1] === '-';
            let whole = match[2];
            let fraction = match[3] || '';
            if (fraction.length > digits) {
                const roundUp = fraction.charAt(digits) >= '5';
                fraction = fraction.slice(0, digits);
                if (roundUp) {
                    const bumped = this.addOneDecimal(whole + fraction);
                    if (digits === 0) {
                        whole = bumped;
                        fraction = '';
                    } else {
                        whole = bumped.slice(0, bumped.length - digits) || '0';
                        fraction = bumped.slice(bumped.length - digits);
                    }
                }
            } else if (digits > 0) {
                fraction = fraction.padEnd(digits, '0');
            }
            whole = whole.replace(/^0+(?=\d)/, '') || '0';
            const body = digits === 0 ? whole : whole + '.' + fraction;
            if (negative && !/^0(\.0+)?$/.test(body)) return '-' + body;
            return body;
        },
        addOneDecimal(digits) {
            const chars = digits.split('');
            let index = chars.length - 1;
            while (index >= 0 && chars[index] === '9') {
                chars[index] = '0';
                index -= 1;
            }
            if (index < 0) chars.unshift('1');
            else chars[index] = String(Number(chars[index]) + 1);
            return chars.join('');
        },
        amountText(value, row) {
            return this.formatAmount(value, this.scaleOf(row));
        },
        emptyUserForm() {
            return {
                id: '',
                userEmail: '',
                userPassword: '',
                packageIds: [],
                signatures: '',
                description: '',
                isEnable: true,
                isAdmin: false
            };
        },
        switchTab(tab) {
            this.activeTab = tab;
            if (tab === 'log') this.syncLogTableHeight();
            this.loadTab();
        },
        syncLogTableHeight() {
            this.$nextTick(() => {
                const table = this.$refs.logTable;
                const el = table && table.$el;
                const top = el && el.getBoundingClientRect ? el.getBoundingClientRect().top : 0;
                if (!top) {
                    this.logTableHeight = Math.max(240, window.innerHeight - 460);
                    return;
                }
                const pager = 56;
                const gap = 36;
                this.logTableHeight = Math.max(240, Math.floor(window.innerHeight - top - pager - gap));
            });
        },
        refresh() {
            this.loadCards().then(() => this.loadTab());
        },
        onCardChange() {
            this.logPage.page = 1;
            this.loadTab();
        },
        selectCard(row) {
            if (this.selectedCardId === row.id) return;
            this.selectedCardId = row.id;
            this.packageList = [];
            this.userList = [];
            this.logList = [];
        },
        openPackages(row) {
            this.selectedCardId = row.id;
            this.switchTab('package');
        },
        openUsers(row) {
            this.selectedCardId = row.id;
            this.switchTab('user');
        },
        loadTab() {
            if (this.activeTab === 'package') return this.loadPackages();
            if (this.activeTab === 'user') {
                return Promise.all([this.loadPackages(), this.loadUsers()]);
            }
            if (this.activeTab === 'log') return this.loadLogs();
            return Promise.resolve();
        },
        needCard() {
            if (this.selectedCardId) return true;
            ElMessage({ message: '请先选择一张权限卡', type: 'warning', duration: 2000 });
            return false;
        },
        loadCards() {
            this.loading = this.activeTab === 'card';
            return request({
                url: '/card/list',
                method: 'post',
                data: {}
            }).then(res => {
                this.cardList = res.meta.cards || [];
                const exists = this.cardList.some(item => item.id === this.selectedCardId);
                if (!exists) this.selectedCardId = '';
            }).finally(() => {
                if (this.activeTab === 'card') this.loading = false;
            });
        },
        loadPackages() {
            if (!this.selectedCardId) {
                this.packageList = [];
                return Promise.resolve();
            }
            this.loading = this.activeTab === 'package';
            return request({
                url: '/card/package/list',
                method: 'post',
                data: { cardId: this.selectedCardId }
            }).then(res => {
                this.packageList = res.meta.packages || [];
            }).finally(() => {
                if (this.activeTab === 'package') this.loading = false;
            });
        },
        loadUsers() {
            if (!this.selectedCardId) {
                this.userList = [];
                return Promise.resolve();
            }
            this.loading = this.activeTab === 'user';
            return request({
                url: '/card/user/list',
                method: 'post',
                data: { cardId: this.selectedCardId }
            }).then(res => {
                this.userList = res.meta.users || [];
            }).finally(() => {
                if (this.activeTab === 'user') this.loading = false;
            });
        },
        searchLogs() {
            this.logPage.page = 1;
            return this.loadLogs();
        },
        changeLogPage(page) {
            if (page === this.logPage.page) return;
            this.logPage.page = page;
            return this.loadLogs();
        },
        loadLogs() {
            if (!this.selectedCardId) {
                this.logList = [];
                this.logPage.total = 0;
                return Promise.resolve();
            }
            this.loading = true;
            const data = {
                cardId: this.selectedCardId,
                page: this.logPage.page,
                pageSize: this.logPage.pageSize
            };
            ['category', 'bizTimeStart', 'bizTimeEnd', 'createTimeStart', 'createTimeEnd'].forEach(key => {
                if (this.logQuery[key]) data[key] = this.logQuery[key];
            });
            return request({
                url: '/card/log/list',
                method: 'post',
                data: data
            }).then(res => {
                this.logList = res.meta.logs || [];
                const total = res.meta && res.meta.total != null ? res.meta.total : res.total;
                this.logPage.total = Number(total) || 0;
                if (res.meta && res.meta.page) this.logPage.page = Number(res.meta.page);
            }).finally(() => {
                this.loading = false;
                this.syncLogTableHeight();
            });
        },
        openCreate() {
            if (this.activeTab === 'card') this.openCreateCard();
            else if (this.activeTab === 'package') this.openCreatePackage();
            else if (this.activeTab === 'user') this.openCreateUser();
        },
        openCreateCard() {
            this.cardForm = { id: '', name: '', description: '', inviteCode: '' };
            this.cardVisible = true;
        },
        openEditCard(row) {
            this.cardForm = { id: row.id, name: row.name, description: row.description || '', inviteCode: row.inviteCode || '' };
            this.cardVisible = true;
        },
        saveCard() {
            this.$refs.cardFormRef.validate(valid => {
                if (!valid) return;
                this.saving = true;
                const editing = !!this.cardForm.id;
                request({
                    url: editing ? '/card/edit' : '/card/create',
                    method: 'post',
                    data: {
                        id: this.cardForm.id,
                        name: this.cardForm.name,
                        description: this.cardForm.description,
                        inviteCode: this.cardForm.inviteCode
                    }
                }).then(res => {
                    ElMessage({ message: '已保存', type: 'success', duration: 1500 });
                    this.cardVisible = false;
                    if (!editing && res.meta.card) this.selectedCardId = res.meta.card.id;
                    this.loadCards();
                }).finally(() => {
                    this.saving = false;
                });
            });
        },
        deleteCard(row) {
            request({
                url: '/card/delete',
                method: 'post',
                data: { id: row.id }
            }).then(() => {
                ElMessage({ message: '已删除', type: 'success', duration: 1500 });
                if (this.selectedCardId === row.id) this.selectedCardId = '';
                this.loadCards();
            });
        },
        openCreatePackage() {
            if (!this.needCard()) return;
            this.packageForm = this.emptyPackageForm();
            this.packageVisible = true;
        },
        openEditPackage(row) {
            this.packageForm = {
                id: row.id,
                name: row.name,
                description: row.description || '',
                isEnable: !!row.isEnable,
                isAllowApply: !!row.isAllowApply,
                allowEmail: row.allowEmail || '',
                allowEmailSuffix: row.allowEmailSuffix || '',
                packageDataText: this.prettyJson(row.packageData),
                quotaScale: this.scaleOf(row),
                userQuota: this.amountText(row.userQuota, row),
                sharedQuota: this.amountText(row.sharedQuota, row)
            };
            this.packageVisible = true;
        },
        savePackage() {
            this.$refs.packageFormRef.validate(valid => {
                if (!valid) return;
                const packageData = this.parsePackageData(this.packageForm.packageDataText);
                if (!packageData) {
                    ElMessage({ message: '配置需要是 JSON 对象', type: 'warning', duration: 2000 });
                    return;
                }
                this.saving = true;
                const editing = !!this.packageForm.id;
                request({
                    url: editing ? '/card/package/edit' : '/card/package/create',
                    method: 'post',
                    data: {
                        id: this.packageForm.id,
                        cardId: this.selectedCardId,
                        name: this.packageForm.name,
                        description: this.packageForm.description,
                        isEnable: this.packageForm.isEnable,
                        isAllowApply: this.packageForm.isAllowApply,
                        allowEmail: this.packageForm.allowEmail,
                        allowEmailSuffix: this.packageForm.allowEmailSuffix,
                        packageData: packageData,
                        quotaScale: this.packageForm.quotaScale,
                        userQuota: this.packageForm.userQuota,
                        sharedQuota: this.packageForm.sharedQuota
                    }
                }).then(() => {
                    ElMessage({ message: '已保存', type: 'success', duration: 1500 });
                    this.packageVisible = false;
                    this.loadPackages();
                    this.loadCards();
                }).finally(() => {
                    this.saving = false;
                });
            });
        },
        enablePackage(row) {
            const enable = row.isEnable;
            row._enableLoading = true;
            request({
                url: '/card/package/enable',
                method: 'post',
                data: { id: row.id, enable: enable }
            }).then(() => {
                ElMessage({ message: enable ? '资源包已启用' : '资源包已停用', type: 'success', duration: 1500 });
            }).catch(() => {
                row.isEnable = !enable;
            }).finally(() => {
                row._enableLoading = false;
            });
        },
        unlockPackage(row) {
            request({
                url: '/card/package/unlock',
                method: 'post',
                data: { id: row.id }
            }).then(() => {
                ElMessage({ message: '已解锁', type: 'success', duration: 1500 });
                this.loadPackages();
            });
        },
        deletePackage(row) {
            request({
                url: '/card/package/delete',
                method: 'post',
                data: { id: row.id }
            }).then(() => {
                ElMessage({ message: '已删除', type: 'success', duration: 1500 });
                this.loadPackages();
                this.loadCards();
            });
        },
        openCreateUser() {
            if (!this.needCard()) return;
            if (this.packageList.length === 0) {
                this.loadPackages().then(() => {
                    if (this.packageList.length === 0) {
                        ElMessage({ message: '请先创建资源包', type: 'warning', duration: 2000 });
                        return;
                    }
                    this.userForm = this.emptyUserForm();
                    this.userVisible = true;
                });
                return;
            }
            this.userForm = this.emptyUserForm();
            this.userVisible = true;
        },
        openEditUser(row) {
            this.userForm = {
                id: row.id,
                userEmail: row.userEmail,
                userPassword: '',
                packageIds: this.idArray(row.packageIds),
                signatures: row.signatures || '',
                description: row.description || '',
                isEnable: !!row.isEnable,
                isAdmin: !!row.isAdmin
            };
            if (this.packageList.length === 0) this.loadPackages();
            this.userVisible = true;
        },
        saveUser() {
            this.$refs.userFormRef.validate(valid => {
                if (!valid) return;
                this.saving = true;
                const editing = !!this.userForm.id;
                request({
                    url: editing ? '/card/user/edit' : '/card/user/create',
                    method: 'post',
                    data: {
                        id: this.userForm.id,
                        cardId: this.selectedCardId,
                        userEmail: this.userForm.userEmail,
                        userPassword: this.userForm.userPassword,
                        packageIds: this.userForm.packageIds,
                        signatures: this.userForm.signatures,
                        description: this.userForm.description,
                        isEnable: this.userForm.isEnable,
                        isAdmin: this.userForm.isAdmin
                    }
                }).then(res => {
                    this.userVisible = false;
                    const password = res.meta.user && res.meta.user.userPassword;
                    if (!editing && password) {
                        ElMessageBox.alert(password, '用户已创建，请保存密码', { confirmButtonText: '知道了' });
                    } else {
                        ElMessage({ message: '已保存', type: 'success', duration: 1500 });
                    }
                    this.loadUsers();
                    this.loadCards();
                }).finally(() => {
                    this.saving = false;
                });
            });
        },
        enableUser(row) {
            const enable = row.isEnable;
            row._enableLoading = true;
            request({
                url: '/card/user/enable',
                method: 'post',
                data: { id: row.id, enable: enable }
            }).then(() => {
                ElMessage({ message: enable ? '用户已启用' : '用户已停用', type: 'success', duration: 1500 });
            }).catch(() => {
                row.isEnable = !enable;
            }).finally(() => {
                row._enableLoading = false;
            });
        },
        resetPassword(row) {
            request({
                url: '/card/user/resetPassword',
                method: 'post',
                data: { id: row.id }
            }).then(res => {
                const password = res.meta.user && res.meta.user.userPassword;
                ElMessageBox.alert(password || '', '密码已重置', { confirmButtonText: '知道了' });
                this.loadUsers();
            });
        },
        deleteUser(row) {
            request({
                url: '/card/user/delete',
                method: 'post',
                data: { id: row.id }
            }).then(() => {
                ElMessage({ message: '已删除', type: 'success', duration: 1500 });
                this.loadUsers();
                this.loadCards();
            });
        },
        idArray(value) {
            if (!value) return [];
            if (Array.isArray(value)) return value.map(item => String(item));
            if (typeof value === 'string') {
                const text = value.trim();
                if (!text) return [];
                try {
                    const parsed = JSON.parse(text);
                    if (Array.isArray(parsed)) return parsed.map(item => String(item));
                } catch (e) {
                    return text.split(',').map(item => item.trim()).filter(Boolean);
                }
            }
            return [];
        },
        packageNames(packageIds) {
            const ids = this.idArray(packageIds);
            return ids.map(id => {
                const found = this.packageList.find(item => item.id === id);
                return found ? found.name : id;
            });
        },
        parsePackageData(text) {
            const source = (text || '').trim();
            if (!source) return {};
            try {
                const parsed = JSON.parse(source);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
            } catch (e) {
                return null;
            }
            return null;
        },
        prettyJson(value) {
            if (value == null || value === '') return '{}';
            try {
                const parsed = typeof value === 'string' ? JSON.parse(value) : value;
                return JSON.stringify(parsed, null, 2);
            } catch (e) {
                return String(value);
            }
        },
        openEditLog(row) {
            this.logForm = {
                id: row.id,
                delta: this.amountText(row.delta, row),
                remark: row.remark || '',
                bizTime: this.formatTime(row.bizTime || row.createTime)
            };
            this.logVisible = true;
        },
        saveLog() {
            this.saving = true;
            request({
                url: '/card/log/edit',
                method: 'post',
                data: {
                    id: this.logForm.id,
                    delta: this.logForm.delta,
                    remark: this.logForm.remark,
                    bizTime: this.logForm.bizTime
                }
            }).then(() => {
                ElMessage({ message: '已保存', type: 'success', duration: 1500 });
                this.logVisible = false;
                this.loadLogs();
            }).finally(() => {
                this.saving = false;
            });
        },
        historyCount(row) {
            return this.historyList(row).length;
        },
        historyList(row) {
            const list = row && row.editHistory ? row.editHistory.slice() : [];
            return list.sort((a, b) => String(b.time || '').localeCompare(String(a.time || '')));
        },
        openHistory(row) {
            this.historyRow = row;
            this.historyVisible = true;
        },
        fieldLabel(field) {
            if (field === 'delta') return '次数';
            if (field === 'remark') return '备注';
            if (field === 'bizTime') return '登记时间';
            return field || '内容';
        },
        historyValue(item, key) {
            const value = item ? item[key] : '';
            if (value === null || value === undefined || value === '') return '空';
            if (item.field === 'delta') {
                const shown = this.formatAmount(value, this.scaleOf(this.historyRow));
                return shown === '' ? '空' : shown;
            }
            return String(value);
        },
        formatTime(value) {
            if (!value) return '';
            return String(value).replace('T', ' ').slice(0, 19);
        },
        isExpanded(row) {
            return this.expandedIds.indexOf(row.id) >= 0;
        },
        toggleExpand(row) {
            const index = this.expandedIds.indexOf(row.id);
            if (index >= 0) this.expandedIds.splice(index, 1);
            else this.expandedIds.push(row.id);
        },
        copy(text, tip) {
            if (!text) return;
            copyText(text);
            ElMessage({ message: tip || '已复制', type: 'success', duration: 1000 });
        }
    }
};
</script>

<style scoped>
.card-page {
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;
    --card-text: #1f2d3d;
    --card-sub: #5b6a80;
    --card-muted: #aab4c4;
    --card-line: #eef1f6;
    --card-border: #e6eaf0;
    color: var(--card-text);
}

.card-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    row-gap: 10px;
    padding: 10px 16px;
    border: 1px solid #ebeef5;
    border-radius: 12px;
    background: linear-gradient(180deg, #ffffff 0%, #fafbfd 100%);
    box-shadow: 0 10px 28px -22px rgba(31, 45, 61, 0.55);
}

.card-toolbar__info,
.card-toolbar__actions,
.row-item__head,
.row-item__meta,
.row-item__actions {
    display: flex;
    align-items: center;
}

.card-toolbar__info {
    min-width: 0;
}

.card-toolbar__icon,
.row-item__icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: linear-gradient(135deg, #6a9cff 0%, #2f6fed 100%);
}

.card-toolbar__icon {
    width: 34px;
    height: 34px;
    margin-right: 10px;
    border-radius: 10px;
    font-size: 18px;
}

.card-toolbar__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.card-toolbar__title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
}

.card-toolbar__subtitle {
    font-size: 12px;
    line-height: 16px;
    color: #a8abb2;
}

.card-toolbar__actions,
.row-item__actions {
    flex-wrap: wrap;
    gap: 8px;
}

.card-picker {
    width: 200px;
}

.toolbar-btn {
    height: 34px;
    padding: 0 16px;
    border-radius: 9px;
    font-weight: 500;
}

.toolbar-btn--ghost {
    color: #606266;
    background-color: #fff;
    border-color: #e4e7ed;
}

.btn-icon {
    margin-right: 4px;
}

.card-switch {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
}

.card-switch__item {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 18px;
    border: 1px solid var(--card-border);
    border-radius: 10px;
    background-color: #fff;
    color: var(--card-sub);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
}

.card-switch__item.is-active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #2f6fed 0%, #2659be 100%);
}

.card-switch__count {
    padding: 0 8px;
    border-radius: 999px;
    background-color: #f2f5fa;
    color: var(--card-sub);
    font-size: 12px;
    font-style: normal;
    line-height: 18px;
}

.card-switch__item.is-active .card-switch__count {
    background-color: rgba(255, 255, 255, 0.24);
    color: #fff;
}

.card-panel {
    margin-top: 12px;
    padding: 6px;
    border: 1px solid var(--card-border);
    border-radius: 14px;
    background-color: #fff;
}

.row-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px;
    border-radius: 10px;
}

.row-item + .row-item {
    border-top: 1px solid var(--card-line);
}

.row-item:hover,
.row-item.is-selected {
    background-color: #f7faff;
}

.row-item__icon {
    width: 38px;
    height: 38px;
    border-radius: 11px;
    font-size: 19px;
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
}

.row-item.is-off .row-item__icon {
    color: #8a94a6;
    background-color: #f2f5fa;
}

.row-item__main {
    flex: 1 1 auto;
    min-width: 0;
}

.row-item__head,
.row-item__meta {
    flex-wrap: wrap;
    gap: 8px;
}

.row-item__name {
    font-size: 15px;
    font-weight: 600;
}

.row-item__desc,
.row-item__time {
    color: var(--card-sub);
    font-size: 13px;
}

.log-filter {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 12px 0;
}

.log-type {
    width: 140px;
}

.log-table {
    width: 100%;
}

.log-pagination {
    display: flex;
    justify-content: flex-end;
    margin: 12px 0 4px;
}

.log-history-empty {
    color: var(--card-muted);
}

.log-history-meta {
    margin-bottom: 16px;
    color: var(--card-sub);
    font-size: 13px;
}

.log-history {
    padding-left: 4px;
}

.row-item__desc {
    margin: 4px 0;
}

.row-item__time {
    margin-left: auto;
}

.card-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 999px;
    background-color: #f2f5fa;
    color: var(--card-sub);
    font-size: 12px;
    line-height: 18px;
}

.card-chip--on {
    background-color: #eaf7ef;
    color: #2ba471;
}

.card-chip--off {
    background-color: #f2f3f5;
    color: #909399;
}

.card-chip--count {
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
}

.card-chip--lock {
    background-color: #fff3e8;
    color: #d46b08;
}

.id-box,
.pwd-box {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 280px;
    padding: 2px 8px;
    border: 1px dashed #d9e2f2;
    border-radius: 8px;
    background: #f8faff;
    color: #4e5969;
    font-size: 12px;
    font-family: Consolas, Monaco, monospace;
}

.id-box {
    cursor: pointer;
}

.id-box span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.json-preview {
    margin: 8px 0;
    padding: 10px 12px;
    border-radius: 8px;
    background: #f7f8fa;
    color: #3d4b5c;
    font-size: 12px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-all;
}

.json-preview.is-collapsed {
    max-height: 42px;
    overflow: hidden;
}

.json-input :deep(textarea) {
    font-family: Consolas, Monaco, monospace;
}

.form-tip {
    margin-left: 8px;
    color: #a8abb2;
    font-size: 12px;
}

.row-item__actions {
    flex: none;
    justify-content: flex-end;
    max-width: 360px;
}
</style>

<style>
.package-dialog.el-dialog {
    margin-bottom: 12px;
}

.package-dialog .el-dialog__header {
    padding-bottom: 6px;
}

.package-dialog .el-dialog__body {
    padding-top: 6px;
    padding-bottom: 4px;
}

.package-dialog .el-form-item {
    margin-bottom: 12px;
}
</style>
